<template>
  <div class="column content-box">
    <woot-loading-state
      v-if="isLoading"
      :message="$t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.LOADING')"
    />

    <div v-else-if="hasLoadError" class="load-error">
      <p>{{ $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.LOAD_ERROR') }}</p>
      <woot-button variant="smooth" @click="loadPage">
        {{ $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.RETRY') }}
      </woot-button>
    </div>

    <div v-else class="row">
      <div class="small-8 columns with-right-space">
        <div class="form-toolbar">
          <div class="form-toolbar__pipeline">
            <span class="form-toolbar__label">
              {{ $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.PIPELINE_LABEL') }}
            </span>
            <h2 class="form-toolbar__name" :title="pipelineName">
              {{ pipelineName }}
            </h2>
          </div>
          <woot-button
            v-if="negotiationForm"
            class="form-toolbar__action"
            color-scheme="success"
            icon="add-circle"
            @click="openCreateModal"
          >
            {{ $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.NEW_QUESTION') }}
          </woot-button>
          <woot-button
            v-else
            class="form-toolbar__action"
            color-scheme="success"
            icon="add-circle"
            :is-loading="isSaving"
            @click="createForm"
          >
            {{ $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.CREATE_BUTTON') }}
          </woot-button>
        </div>

        <div v-if="!negotiationForm" class="form-card form-card--empty">
          <fluent-icon icon="document" size="40" class="empty-icon" />
          <p class="empty-title">
            {{ $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.EMPTY_TITLE') }}
          </p>
          <p class="empty-message">
            {{ $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.EMPTY_MESSAGE') }}
          </p>
        </div>

        <template v-else>
          <div class="form-card">
            <div class="form-card__header">
              <p class="form-card__title">
                {{
                  $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.QUESTIONS_TITLE')
                }}
                <span v-if="questions.length" class="form-card__count">
                  {{ questions.length }}
                </span>
              </p>
              <label class="status-toggle">
                <span>
                  {{
                    $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.STATUS_LABEL')
                  }}
                </span>
                <woot-switch :value="isActive" @input="toggleStatus" />
              </label>
            </div>

            <p v-if="!isActive" class="inactive-notice">
              {{ $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.INACTIVE_NOTICE') }}
            </p>

            <p v-if="!questions.length" class="questions-empty">
              {{ $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.QUESTIONS_EMPTY') }}
            </p>

            <draggable
              v-else
              v-model="questions"
              tag="ul"
              class="questions-list"
              handle=".drag-handle"
              @end="persistOrder"
            >
              <li
                v-for="(question, index) in questions"
                :key="question.id"
                class="question-row"
              >
                <span class="drag-handle">
                  <fluent-icon icon="drag" size="16" />
                </span>
                <span class="question-position">{{ index + 1 }}</span>
                <div class="question-text">
                  <p class="question-label">{{ question.label }}</p>
                  <p class="question-meta">
                    {{ $t(typeLabelKey(question.question_type)) }}
                    <span class="question-meta__separator">·</span>
                    <span
                      :class="{
                        'question-meta__required':
                          question.required_pipeline_stage,
                      }"
                    >
                      {{ requiredLabel(question) }}
                    </span>
                  </p>
                  <p
                    v-if="question.question_type === 'list'"
                    class="question-options"
                  >
                    {{ question.list_values.join(', ') }}
                  </p>
                </div>
                <div class="question-actions">
                  <woot-button
                    v-tooltip.top="$t('OPPORTUNITY_PIPELINES.ACTIONS.EDIT')"
                    variant="smooth"
                    color-scheme="secondary"
                    icon="edit"
                    size="tiny"
                    @click="openEditModal(question)"
                  />
                  <woot-button
                    v-tooltip.top="$t('OPPORTUNITY_PIPELINES.ACTIONS.DELETE')"
                    variant="smooth"
                    color-scheme="alert"
                    icon="delete"
                    size="tiny"
                    @click="openDeleteQuestionModal(question)"
                  />
                </div>
              </li>
            </draggable>
          </div>

          <div class="form-footer">
            <woot-button
              variant="link"
              color-scheme="alert"
              size="small"
              @click="showDeleteFormModal = true"
            >
              {{ $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.DELETE_FORM') }}
            </woot-button>
          </div>
        </template>
      </div>

      <div class="small-4 columns">
        <span
          v-dompurify-html="
            $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.SIDEBAR_TXT')
          "
        />
      </div>
    </div>

    <woot-modal :show.sync="showQuestionModal" :on-close="closeQuestionModal">
      <question-modal
        :question="selectedQuestion"
        :stages="stages"
        :is-saving="isSaving"
        @submit="saveQuestion"
        @close="closeQuestionModal"
      />
    </woot-modal>

    <woot-delete-modal
      v-if="showDeleteQuestionModal"
      :show.sync="showDeleteQuestionModal"
      :on-close="closeDeleteQuestionModal"
      :on-confirm="confirmDeleteQuestion"
      :title="
        $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.DELETE_QUESTION.TITLE')
      "
      :message="
        $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.DELETE_QUESTION.MESSAGE')
      "
      :confirm-text="
        $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.DELETE_QUESTION.YES')
      "
      :reject-text="$t('OPPORTUNITY_PIPELINES.ACTIONS.CANCEL')"
    />

    <woot-delete-modal
      v-if="showDeleteFormModal"
      :show.sync="showDeleteFormModal"
      :on-close="closeDeleteFormModal"
      :on-confirm="confirmDeleteForm"
      :title="$t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.DELETE_CONFIRM.TITLE')"
      :message="
        $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.DELETE_CONFIRM.MESSAGE')
      "
      :confirm-text="
        $t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.DELETE_CONFIRM.YES')
      "
      :reject-text="$t('OPPORTUNITY_PIPELINES.ACTIONS.CANCEL')"
    />
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import alertMixin from 'shared/mixins/alertMixin';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { sortStagesByPosition } from 'dashboard/helper/opportunityPipelineHelper';
import { negotiationQuestionTypeLabelKey } from 'dashboard/helper/opportunityNegotiationFormHelper';
import QuestionModal from './QuestionModal';

export default {
  components: {
    draggable,
    QuestionModal,
  },
  mixins: [alertMixin],
  data() {
    return {
      isLoading: true,
      hasLoadError: false,
      isSaving: false,
      pipeline: null,
      negotiationForm: null,
      questions: [],
      showQuestionModal: false,
      selectedQuestion: {},
      showDeleteQuestionModal: false,
      questionToDelete: null,
      showDeleteFormModal: false,
    };
  },
  computed: {
    pipelineId() {
      return this.$route.params.pipelineId;
    },
    pipelineName() {
      return this.pipeline ? this.pipeline.name : '';
    },
    stages() {
      return sortStagesByPosition(
        (this.pipeline && this.pipeline.stages) || []
      );
    },
    isActive() {
      return !!this.negotiationForm && this.negotiationForm.status === 'active';
    },
  },
  mounted() {
    this.loadPage();
  },
  methods: {
    typeLabelKey(type) {
      return negotiationQuestionTypeLabelKey(type);
    },
    requiredLabel(question) {
      if (!question.required_pipeline_stage) {
        return this.$t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.OPTIONAL');
      }

      return this.$t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.REQUIRED_FROM', {
        stageName: question.required_pipeline_stage.name,
      });
    },
    applyNegotiationForm(negotiationForm) {
      this.negotiationForm = negotiationForm;
      this.questions = negotiationForm ? [...negotiationForm.questions] : [];
    },
    showError(error, fallbackKey) {
      this.showAlert(parseAPIErrorResponse(error) || this.$t(fallbackKey));
    },
    async loadPage() {
      this.isLoading = true;
      this.hasLoadError = false;
      try {
        const pipeline = await this.$store.dispatch(
          'opportunityPipelines/show',
          this.pipelineId
        );
        const negotiationForm = pipeline.negotiation_form
          ? await this.$store.dispatch(
              'opportunityPipelines/getNegotiationForm',
              this.pipelineId
            )
          : null;
        this.pipeline = pipeline;
        this.applyNegotiationForm(negotiationForm);
      } catch (error) {
        this.hasLoadError = true;
      } finally {
        this.isLoading = false;
      }
    },
    async refreshNegotiationForm() {
      const negotiationForm = await this.$store.dispatch(
        'opportunityPipelines/getNegotiationForm',
        this.pipelineId
      );
      this.applyNegotiationForm(negotiationForm);
    },
    async createForm() {
      this.isSaving = true;
      try {
        const negotiationForm = await this.$store.dispatch(
          'opportunityPipelines/createNegotiationForm',
          this.pipelineId
        );
        this.applyNegotiationForm(negotiationForm);
        this.showAlert(
          this.$t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.SUCCESS.CREATED')
        );
      } catch (error) {
        this.showError(
          error,
          'OPPORTUNITY_PIPELINES.ERRORS.CREATE_NEGOTIATION_FORM'
        );
      } finally {
        this.isSaving = false;
      }
    },
    async toggleStatus(checked) {
      try {
        const negotiationForm = await this.$store.dispatch(
          'opportunityPipelines/updateNegotiationForm',
          {
            pipelineId: this.pipelineId,
            payload: { status: checked ? 'active' : 'inactive' },
          }
        );
        this.applyNegotiationForm(negotiationForm);
      } catch (error) {
        this.showError(
          error,
          'OPPORTUNITY_PIPELINES.ERRORS.TOGGLE_NEGOTIATION_FORM'
        );
      }
    },
    openCreateModal() {
      this.selectedQuestion = {};
      this.showQuestionModal = true;
    },
    openEditModal(question) {
      this.selectedQuestion = { ...question };
      this.showQuestionModal = true;
    },
    closeQuestionModal() {
      this.showQuestionModal = false;
      this.selectedQuestion = {};
    },
    async saveQuestion(payload) {
      this.isSaving = true;
      try {
        if (this.selectedQuestion.id) {
          await this.$store.dispatch(
            'opportunityPipelines/updateNegotiationQuestion',
            {
              pipelineId: this.pipelineId,
              questionId: this.selectedQuestion.id,
              payload,
            }
          );
        } else {
          await this.$store.dispatch(
            'opportunityPipelines/createNegotiationQuestion',
            { pipelineId: this.pipelineId, payload }
          );
        }
        await this.refreshNegotiationForm();
        this.closeQuestionModal();
        this.showAlert(
          this.$t(
            'OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.SUCCESS.QUESTION_SAVED'
          )
        );
      } catch (error) {
        this.showError(
          error,
          'OPPORTUNITY_PIPELINES.ERRORS.SAVE_NEGOTIATION_QUESTION'
        );
      } finally {
        this.isSaving = false;
      }
    },
    openDeleteQuestionModal(question) {
      this.questionToDelete = question;
      this.showDeleteQuestionModal = true;
    },
    closeDeleteQuestionModal() {
      this.showDeleteQuestionModal = false;
      this.questionToDelete = null;
    },
    async confirmDeleteQuestion() {
      const question = this.questionToDelete;
      this.closeDeleteQuestionModal();
      if (!question) {
        return;
      }

      try {
        await this.$store.dispatch(
          'opportunityPipelines/deleteNegotiationQuestion',
          { pipelineId: this.pipelineId, questionId: question.id }
        );
        await this.refreshNegotiationForm();
        this.showAlert(
          this.$t(
            'OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.SUCCESS.QUESTION_DELETED'
          )
        );
      } catch (error) {
        this.showError(
          error,
          'OPPORTUNITY_PIPELINES.ERRORS.DELETE_NEGOTIATION_QUESTION'
        );
      }
    },
    async persistOrder() {
      try {
        const negotiationForm = await this.$store.dispatch(
          'opportunityPipelines/reorderNegotiationQuestions',
          {
            pipelineId: this.pipelineId,
            questionIds: this.questions.map(question => question.id),
          }
        );
        this.applyNegotiationForm(negotiationForm);
      } catch (error) {
        this.showError(
          error,
          'OPPORTUNITY_PIPELINES.ERRORS.REORDER_NEGOTIATION_QUESTIONS'
        );
        await this.refreshNegotiationForm();
      }
    },
    closeDeleteFormModal() {
      this.showDeleteFormModal = false;
    },
    async confirmDeleteForm() {
      this.closeDeleteFormModal();
      try {
        await this.$store.dispatch(
          'opportunityPipelines/deleteNegotiationForm',
          this.pipelineId
        );
        this.applyNegotiationForm(null);
        this.showAlert(
          this.$t('OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.SUCCESS.DELETED')
        );
      } catch (error) {
        this.showError(
          error,
          'OPPORTUNITY_PIPELINES.ERRORS.DELETE_NEGOTIATION_FORM'
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.load-error {
  align-items: center;
  color: var(--s-700);
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
  padding: var(--space-larger) 0;
}

.form-toolbar {
  align-items: flex-end;
  display: flex;
  gap: var(--space-normal);
  justify-content: space-between;
  margin-bottom: var(--space-normal);
}

.form-toolbar__pipeline {
  min-width: 0;
}

.form-toolbar__label {
  color: var(--s-600);
  font-size: var(--font-size-mini);
  text-transform: uppercase;
}

.form-toolbar__name {
  font-size: var(--font-size-medium);
  font-weight: var(--font-weight-medium);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.form-toolbar__action {
  flex-shrink: 0;
  white-space: nowrap;
}

.form-card {
  background: var(--white);
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-medium);
}

.form-card--empty {
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: var(--space-larger) var(--space-large);
  text-align: center;
}

.empty-icon {
  color: var(--s-400);
  margin-bottom: var(--space-small);
}

.empty-title {
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--space-smaller);
}

.empty-message {
  color: var(--s-700);
  font-size: var(--font-size-small);
  margin: 0;
  max-width: 44rem;
}

.form-card__header {
  align-items: center;
  border-bottom: 1px solid var(--color-border-light);
  display: flex;
  justify-content: space-between;
  padding: var(--space-small) var(--space-normal);
}

.form-card__title {
  align-items: center;
  display: flex;
  font-weight: var(--font-weight-medium);
  gap: var(--space-smaller);
  margin: 0;
}

.form-card__count {
  background: var(--s-50);
  border-radius: 1rem;
  color: var(--s-700);
  font-size: var(--font-size-mini);
  padding: 0 var(--space-small);
}

.status-toggle {
  align-items: center;
  color: var(--s-700);
  display: flex;
  font-size: var(--font-size-small);
  gap: var(--space-small);
  margin: 0;
}

.inactive-notice {
  background: var(--y-50);
  color: var(--y-800);
  font-size: var(--font-size-mini);
  margin: 0;
  padding: var(--space-small) var(--space-normal);
}

.questions-empty {
  color: var(--s-600);
  font-size: var(--font-size-small);
  margin: 0;
  padding: var(--space-normal);
}

.questions-list {
  list-style: none;
  margin: 0;
}

.question-row {
  align-items: center;
  border-bottom: 1px solid var(--color-border-light);
  display: flex;
  gap: var(--space-small);
  padding: var(--space-small) var(--space-normal);

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: var(--s-25);
  }
}

.drag-handle {
  color: var(--s-400);
  cursor: move;
  display: flex;
}

.question-position {
  color: var(--s-600);
  flex-shrink: 0;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  min-width: var(--space-normal);
  text-align: right;
}

.question-text {
  flex: 1;
  min-width: 0;
}

.question-label {
  margin: 0;
  overflow-wrap: anywhere;
}

.question-meta,
.question-options {
  color: var(--s-600);
  font-size: var(--font-size-mini);
  margin: 0;
  overflow-wrap: anywhere;
}

.question-meta__separator {
  margin: 0 var(--space-micro);
}

.question-meta__required {
  color: var(--y-800);
  font-weight: var(--font-weight-medium);
}

.question-actions {
  display: flex;
  flex-shrink: 0;
  gap: var(--space-smaller);
}

.form-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: var(--space-small);
}

@media screen and (max-width: 768px) {
  .form-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
